import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useSession } from "@/frontend/auth-client";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Separator } from "@/frontend/components/ui/separator";
import { Loader2 } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/frontend/components/ui/dialog";
import {
  useAdminListUsersQuery,
} from "@/frontend/queries/dashboard/settings";
import {
  useDeleteUserMutation,
  usePreprovisionUserMutation,
  useSetUserBetaFlagMutation,
} from "@/frontend/mutations/dashboard/settings";
import { Switch } from "@/frontend/components/ui/switch";

export function UserManagementSection() {
  const { data: session } = useSession();
  const [searchQuery, setSearchQuery] = useState("");
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string; email: string } | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserIsBeta, setNewUserIsBeta] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);

  const isPlatformAdmin = session?.user?.role === "admin";
  const { data: users, isLoading } = useAdminListUsersQuery();

  const deleteUserMutation = useDeleteUserMutation(() => setUserToDelete(null));
  const betaFlagMutation = useSetUserBetaFlagMutation();
  const preprovisionMutation = usePreprovisionUserMutation(() => {
    setShowAddUser(false);
    setNewUserEmail("");
    setNewUserIsBeta(false);
    setAddUserError(null);
  });

  /** Opens the add-user dialog with a clean slate. */
  const openAddUser = () => {
    setNewUserEmail("");
    setNewUserIsBeta(false);
    setAddUserError(null);
    setShowAddUser(true);
  };

  /** Submits the pre-provision form; surfaces API errors inline. */
  const handleAddUser = (e: FormEvent) => {
    e.preventDefault();
    setAddUserError(null);
    preprovisionMutation.mutate(
      { email: newUserEmail.trim(), isBeta: newUserIsBeta },
      { onError: (error) => setAddUserError(error.message || "Failed to create user") }
    );
  };

  if (!isPlatformAdmin) {
    return (
      <div className="py-8">
        <p className="text-sm text-muted-foreground">Platform admin access required.</p>
      </div>
    );
  }

  const allUsers = users ?? [];
  const filteredUsers = allUsers.filter(
    (u) =>
      (u.name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">User Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage platform users: pre-provision accounts by email, flag beta users, and permanently delete accounts.
          </p>
        </div>
        <Button size="sm" onClick={openAddUser}>Add user</Button>
      </div>

      <Separator />

      <div>
        <Input
          placeholder="Search by name or email…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">User</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Beta</th>
                <th className="px-4 py-2.5 w-24" />
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium" data-ph-mask>{u.name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground" data-ph-mask>{u.email}</td>
                  <td className="px-4 py-3">
                    <Switch
                      size="sm"
                      checked={!!u.isBeta}
                      disabled={betaFlagMutation.isPending && betaFlagMutation.variables?.userId === u.id}
                      onCheckedChange={(isBeta) =>
                        betaFlagMutation.mutate(
                          { userId: u.id, isBeta },
                          { onError: (error) => toast.error(error.message || "Failed to update beta flag") }
                        )
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    {u.id !== session?.user?.id && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs"
                        onClick={() => setUserToDelete({ id: u.id, name: u.name ?? "—", email: u.email })}
                      >
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              Create a user from just their email. They complete their profile when they first sign in with this address.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="flex flex-col gap-4">
            <Input
              type="email"
              required
              placeholder="user@example.com"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              autoFocus
            />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Beta user</p>
                <p className="text-xs text-muted-foreground">Gets access to beta features.</p>
              </div>
              <Switch checked={newUserIsBeta} onCheckedChange={setNewUserIsBeta} />
            </div>
            {addUserError && <p className="text-sm text-destructive">{addUserError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={preprovisionMutation.isPending || !newUserEmail.trim()}>
                {preprovisionMutation.isPending ? (
                  <><Loader2 className="size-4 animate-spin mr-2" />Creating…</>
                ) : (
                  "Create user"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong data-ph-mask>{userToDelete?.name}</strong> (
              <span data-ph-mask>{userToDelete?.email}</span>)? This removes their account from the platform entirely and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => userToDelete && deleteUserMutation.mutate({ userId: userToDelete.id })}
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? (
                <><Loader2 className="size-4 animate-spin mr-2" />Deleting…</>
              ) : (
                "Delete user"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
